import React from 'react';
import styled from 'styled-components';
import { flattenDeep } from 'lodash';

import Text from 'relements/build/esm/input/text';
import Radio from 'relements/build/esm/input/radio';
import Toggle from 'relements/build/esm/input/toggle';
import Dropdown from 'relements/build/esm/input/dropdown';
import Box from 'relements/build/esm/ui/box';
import TextDisplay from 'relements/build/esm/ui/text';
import Tooltip from 'relements/build/esm/overlays/tooltip';
import Accordion from 'relements/build/esm/ui/accordion';
import InfoIconV4 from 'relements/build/esm/ui/icons/InfoIconV4';
import InvisibleIconV2 from 'relements/build/esm/ui/icons/InvisibleIconV2';
import VisibleIconV2 from 'relements/build/esm/ui/icons/VisibleIconV2';

import Slider from '@src/components/Slider';

import { sendGoogleAnalyticsEvents } from '@src/actions/_utils';
import BotStore from '@src/store';
import CommonStore from '@src/stores/CommonStore';
import { GOOGLE_ANALYTICS, FIELD_TO_GA_LABEL_MAPPING } from '@src/const';
import { FEATURE_FLAG_DATA } from '@src/constants/MogamboConstants';
import { getVoiceModels } from '@src/containers/AIAgents/store/api';
import { BOT_ENV, speedValuesBasedOnTtsProvider } from './constants';
import { keyToStateMap, SUBTLE_NOD_FREQUENCY_OPTIONS } from './utils';

const STATUS_ACTIVE = { title: 'Active', value: true };
const STATUS_INACTIVE = { title: 'Inactive', value: false };

const TextArea = styled(Text)`
  textarea {
    max-height: 54px;
    overflow-y: auto;
    border: 1px solid
      ${(props) =>
        props.error
          ? props.theme.colors.red.haptik
          : props.theme.colors.grey.haptik};
  }
`;

export default class Advanced extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      errorKeys: [],
      dataFetched: false,
      active: STATUS_ACTIVE,
      gender: 'Male',
      companyName: '',
      botPersona: 0,
      botContext: '',
      companyContext: '',
      contextEnhancementInstr: '',
      intentDetectionInstr: '',
      answeringInstr: '',
      aiAgentAuthEnabled: false,
      aiAgentSessionTimeout: null,
      aiAgentSessionTimeoutEnabled: false,
      addDateAndTimeInPrompt: true,
      detectLanguageForAllMessages: false,
      aiAgentVoiceEnabled: false,
      aIAgentVoiceModel: '',
      aiAgentTtsEnabled: false,
      aiAgentPhoneNumber: '',
      aiAgentGreetingMessage: '',
      aiAgentVoiceForOutput: '',
      aiAgentVoiceLocale: '',
      aiAgentVoiceProvider: '',
      aiAgentVoiceModelOptions: [],
      aiAgentVoiceOutputOptions: [],
      ttsVoiceOutputOptions: [],
      aiAgentVoiceLocaleOptions: [],
      aiAgentsModelsData: [],
      aIAgentOptionDisplayStyle: {},
      clearContextOnChatCompletion: false,
      aiAgentOzonetelSipNumber: '',
      telephonyPlatformOptions: [],
      aiAgentVoiceTelephonyPlatform: {},
      aiAgentVoiceSubtleNodFrequency: {},
      ttsProviderSpeed: '',
      ttsProviderStability: '',
      ttsProviderStyle: '',
      ttsProviderSimilarityBoost: '',
      ttsProviderUseSpeakerBoost: false,
      aiAgentTextPipelineEnabled: false,
      aiAgentHumanAgentTransferEnabled: false,
      aiAgentToolCallExecutionEnabled: false,
      aiAgentBackgroundNoiseVolume: 0.1,
      voiceFreePbxAriUrl: '',
      voiceFreePbxAriUsername: '',
      voiceFreePbxAriPassword: '',
      voiceFreePbxExtension: '',
      showVoiceFreePbxAriPassword: false,
      botSettings: {},
      dynamicSettings: [],
    };
    // ref to the wrapper around the FreePBX password Text component
    this.voiceFreePbxAriPasswordWrapperRef = React.createRef();
  }

  componentDidMount() {
    if (this.props.active) {
      this.fetchData();
    }
    this.setVoiceFreePbxAriPasswordInputType();
  }

  componentDidUpdate() {
    if (this.props.active && !this.state.dataFetched) {
      this.setState({ dataFetched: true });
      this.fetchData();
    }
    this.setVoiceFreePbxAriPasswordInputType();
  }

  // Toggle the underlying input's type attribute to show/hide password
  setVoiceFreePbxAriPasswordInputType = () => {
    const wrapper = this.voiceFreePbxAriPasswordWrapperRef?.current;
    if (!wrapper) return;
    const inputEl =
      wrapper.querySelector('input') || wrapper.querySelector('textarea');
    if (inputEl) {
      try {
        inputEl.type = this.state.showVoiceFreePbxAriPassword
          ? 'text'
          : 'password';
      } catch (e) {
        // Some custom inputs may not allow direct type changes; ignore errors.
        console.log('error', e);
      }
    }
  };

  onUpdate = (key, value, fromDynamicSettings) => {
    if (key === 'aIAgentVoiceModel') {
      const { aiAgentsModelsData, ttsVoiceOutputOptions } = this.state;
      const aiAgentVoiceProvider = aiAgentsModelsData?.find(
        (model) => model.id === value.id
      )?.meta_data.provider;
      const aiAgentVoiceOutputOptions = aiAgentsModelsData?.reduce(
        (result, model) => {
          if (model.id !== value.id) return result;
          const selectedModelVoices = model.meta_data?.voices?.map((voice) => {
            return { id: voice, name: voice };
          });
          return [...result, ...selectedModelVoices];
        },
        []
      );
      const aiAgentVoiceLocaleOptions = aiAgentsModelsData?.reduce(
        (result, model) => {
          if (model.id !== value.id) return result;
          const selectedModelLocales = model.meta_data.locales
            ? Object.entries(model.meta_data.locales).map(([key, value]) => {
                return { id: value, name: key };
              })
            : [];
          return [...result, ...selectedModelLocales];
        },
        []
      );
      this.setState({
        [key]: value,
        aiAgentVoiceProvider,
        aiAgentVoiceOutputOptions: [
          ...aiAgentVoiceOutputOptions,
          ...ttsVoiceOutputOptions,
        ],
        aiAgentVoiceLocaleOptions,
        aiAgentVoiceForOutput: '',
        aiAgentVoiceLocale: '',
        aiAgentTtsEnabled: false,
      });
    } else if (key === 'aiAgentVoiceForOutput') {
      if ('ttsProviderId' in value) {
        this.setState({
          [key]: value,
          aiAgentTtsEnabled: true,
          ttsProviderSpeed:
            speedValuesBasedOnTtsProvider?.[value.ttsProviderId]?.default,
        });
      } else {
        this.setState({ [key]: value, aiAgentTtsEnabled: false });
      }
    } else if (fromDynamicSettings) {
      const { botSettings } = this.state;
      botSettings[key] = value;
      this.setState({ botSettings });
    } else {
      this.setState({ [key]: value });
    }

    if (FIELD_TO_GA_LABEL_MAPPING[key]) {
      sendGoogleAnalyticsEvents(
        GOOGLE_ANALYTICS.CATEGORY.BOT,
        GOOGLE_ANALYTICS.EVENTS.BOT_EDIT,
        FIELD_TO_GA_LABEL_MAPPING[key],
        GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
      );
    }
  };

  validate = () => {
    const errorKeys = [];
    const {
      companyContext,
      botContext,
      contextEnhancementInstr,
      intentDetectionInstr,
      answeringInstr,
      aiAgentSessionTimeout,
      aiAgentSessionTimeoutEnabled,
      aiAgentPhoneNumber,
      aiAgentTextPipelineEnabled,
      aiAgentVoiceForOutput,
      aiAgentTtsEnabled,
      aiAgentVoiceTelephonyPlatform,
      voiceFreePbxAriUrl,
      voiceFreePbxAriUsername,
      voiceFreePbxAriPassword,
      voiceFreePbxExtension,
      dynamicSettings,
      botSettings,
    } = this.state;
    const timeoutValueRegex = /^\d+$/;
    const phoneNumberValueRegex = /^\+?\d{7,15}$/;

    if (companyContext && companyContext.split(' ').length > 300)
      errorKeys.push('companyContext');
    if (
      contextEnhancementInstr &&
      contextEnhancementInstr.trim().split(/\s+/).length > 300
    )
      errorKeys.push('contextEnhancementInstr');
    if (
      intentDetectionInstr &&
      intentDetectionInstr.trim().split(/\s+/).length > 300
    )
      errorKeys.push('intentDetectionInstr');
    if (answeringInstr && answeringInstr.trim().split(/\s+/).length > 300)
      errorKeys.push('answeringInstr');
    if (botContext && botContext.split(' ').length > 300)
      errorKeys.push('botContext');
    if (this.state.active === undefined) errorKeys.push('active');
    if (!this.state.gender) errorKeys.push('gender');
    if (this.state.botPersona === undefined) errorKeys.push('botPersona');
    if (aiAgentSessionTimeout && !timeoutValueRegex.test(aiAgentSessionTimeout))
      errorKeys.push('aiAgentSessionTimeout');
    if (aiAgentPhoneNumber && !phoneNumberValueRegex.test(aiAgentPhoneNumber))
      errorKeys.push('aiAgentPhoneNumber');
    if (
      aiAgentSessionTimeoutEnabled &&
      (!aiAgentSessionTimeout || Number(aiAgentSessionTimeout) < 1)
    )
      errorKeys.push('aiAgentSessionTimeout');

    if (
      aiAgentTextPipelineEnabled &&
      !aiAgentTtsEnabled &&
      !aiAgentVoiceForOutput?.name?.includes('ElevenLabs') &&
      !aiAgentVoiceForOutput?.name?.includes('Azure')
    )
      errorKeys.push('aiAgentVoiceForOutput');

    if (aiAgentVoiceTelephonyPlatform?.name === 'freepbx') {
      if (!voiceFreePbxAriUrl) errorKeys.push('voiceFreePbxAriUrl');
      if (!voiceFreePbxAriUsername) errorKeys.push('voiceFreePbxAriUsername');
      if (!voiceFreePbxAriPassword) errorKeys.push('voiceFreePbxAriPassword');
      if (!voiceFreePbxExtension) errorKeys.push('voiceFreePbxExtension');
    }

    dynamicSettings?.forEach((setting) => {
      const { key, mandatory } = setting;
      if (mandatory && [null, undefined, ''].includes(botSettings[key])) {
        errorKeys.push(key);
      }
    });

    this.setState({ errorKeys });

    return errorKeys.length === 0;
  };

  async fetchData() {
    const domain =
      (CommonStore.getMetaTags().domain?.length &&
        CommonStore.getMetaTags().domain) ||
      JSON.parse(localStorage.getItem('ww_iva'));
    const domainId = domain?.id;
    if (!domainId) return;
    try {
      const { response } = await getVoiceModels();
      if (response.success) {
        const data = response.body.models;
        const ttsData = response.body.tts;
        const telephonyPlatformOptions = response.body.telephony_platforms?.map(
          (platform) => ({ name: platform })
        );
        const aiAgentVoiceModelOptions = data.reduce((result, model) => {
          const newModel = { id: model.id, name: model.name };
          return [...result, newModel];
        }, []);
        const aiAgentVoiceOutputOptions = data.reduce((result, model) => {
          if (model.id !== this.state.aIAgentVoiceModel.id) return result;

          const selectedModelVoices =
            model.meta_data?.voices?.map((voice) => {
              return { id: voice, name: voice };
            }) || [];
          return [...result, ...selectedModelVoices];
        }, []);
        let ttsVoiceOutputOptions = Object.entries(ttsData)
          .map(([ttsProviderId, ttsVoiceData]) => ({
            ...ttsVoiceData,
            ttsProviderId: parseInt(ttsProviderId, 10),
          }))
          .map((voiceData) => {
            const voiceOptions = voiceData.voice_details.map((voice) => ({
              id: voice.voice_id,
              name: `${voice.voice_name} — ${voiceData.provider_name}`,
              ttsProviderId: voiceData.ttsProviderId,
            }));
            return voiceOptions;
          });
        ttsVoiceOutputOptions = flattenDeep(ttsVoiceOutputOptions);
        const aiAgentVoiceLocaleOptions = data.reduce((result, model) => {
          if (model.id !== this.state.aIAgentVoiceModel.id) return result;
          const selectedModelLocales = model.meta_data.locales
            ? Object.entries(model.meta_data.locales).map(([key, value]) => {
                return { id: value, name: key };
              })
            : [];

          return [...result, ...selectedModelLocales];
        }, []);
        let aiAgentVoiceLocale = this.state.aiAgentVoiceLocale;

        aiAgentVoiceLocaleOptions.forEach((locale) => {
          if (locale.id === aiAgentVoiceLocale) {
            aiAgentVoiceLocale = { id: aiAgentVoiceLocale, name: locale.name };
          }
        });
        this.setState({
          aiAgentsModelsData: data,
          aiAgentVoiceModelOptions,
          aiAgentVoiceOutputOptions: [
            ...aiAgentVoiceOutputOptions,
            ...ttsVoiceOutputOptions,
          ],
          aiAgentVoiceLocaleOptions,
          aiAgentVoiceLocale,
          ttsVoiceOutputOptions,
          telephonyPlatformOptions,
        });
      }
    } catch (error) {
      console.error('Error fetching AI agent data:', error);
    }
  }

  render() {
    const {
      aiAgentVoiceEnabled,
      aiAgentVoiceForOutput,
      aiAgentOzonetelSipNumber,
      ttsProviderSimilarityBoost,
      ttsProviderSpeed,
      ttsProviderStability,
      ttsProviderStyle,
      ttsProviderUseSpeakerBoost,
      aiAgentTtsEnabled,
      aiAgentVoiceTelephonyPlatform,
      aiAgentHumanAgentTransferEnabled,
      aiAgentToolCallExecutionEnabled,
      aiAgentBackgroundNoiseVolume,
      voiceFreePbxAriUrl,
      voiceFreePbxAriUsername,
      voiceFreePbxAriPassword,
      voiceFreePbxExtension,
      showVoiceFreePbxAriPassword,
      dynamicSettings,
      botSettings,
      aiAgentTextPipelineEnabled,
      errorKeys,
      aiAgentVoiceModelOptions,
      aIAgentVoiceModel,
      telephonyPlatformOptions,
      aiAgentPhoneNumber,
      aiAgentGreetingMessage,
      aiAgentVoiceOutputOptions,
      aiAgentVoiceLocaleOptions,
      aiAgentVoiceLocale,
      aiAgentVoiceSubtleNodFrequency,
      aiAgentAuthEnabled,
    } = this.state;
    const { active_env } = this.props.botEnv;
    const ENV_OBJ =
      active_env && BOT_ENV.hasOwnProperty(active_env.toUpperCase())
        ? BOT_ENV[active_env.toUpperCase()]
        : BOT_ENV.TEST;
    const isAissIntentEnable = BotStore.getFeatureFlag(
      FEATURE_FLAG_DATA.FLAGS.ENABLE_AISS_INTENT
    );
    const currentDomainInStore = CommonStore.getMetaTags().domain;
    const speedValue =
      speedValuesBasedOnTtsProvider[aiAgentVoiceForOutput?.ttsProviderId];

    return (
      <div>
        <Accordion>
          <Accordion.Item
            active={this.props.active}
            onChange={this.props.onChange}
            renderHeader={this.props.renderHeader}
            className={this.props.className}
            prefixClassName={this.props.prefixClassName}
          >
            <div className="grid-container">
              <div className="grid-label">
                * Bot deployment environment (base URL)
              </div>
              <div className="radio-wrapper">
                <Radio
                  themed
                  prefixClassName="env-options"
                  value={ENV_OBJ}
                  options={Object.values(BOT_ENV)}
                  onChange={(value) =>
                    this.props.handleEnvUpdate(`active_env`, value.value)
                  }
                />
              </div>
              <div className="input-wrapper">
                <Text
                  themed
                  style={{ marginBottom: 8 }}
                  className="url-input"
                  placeholder={BOT_ENV.TEST.placeholder}
                  value={this.props.botEnv[`base_url_${BOT_ENV.TEST.value}`]}
                  onChange={(value) =>
                    this.props.handleEnvUpdate(
                      `base_url_${BOT_ENV.TEST.value}`,
                      value
                    )
                  }
                />
                <Text
                  themed
                  className="url-input"
                  placeholder={BOT_ENV.PRODUCTION.placeholder}
                  value={
                    this.props.botEnv[`base_url_${BOT_ENV.PRODUCTION.value}`]
                  }
                  onChange={(value) =>
                    this.props.handleEnvUpdate(
                      `base_url_${BOT_ENV.PRODUCTION.value}`,
                      value
                    )
                  }
                />
              </div>
            </div>
            <Radio
              themed
              options={this.props.personalities}
              label="* Personality"
              value={this.props.personalities[this.state.botPersona]}
              onChange={(value) => this.onUpdate('botPersona', value.key)}
              error={this.state.errorKeys.indexOf('botPersona') > -1}
              className="bot-properties-input"
            />
            <Radio
              themed
              options={[STATUS_ACTIVE, STATUS_INACTIVE]}
              label="* Bot Status"
              className="bot-properties-input"
              value={this.state.active}
              onChange={(value) => this.onUpdate('active', value)}
              error={this.state.errorKeys.indexOf('active') > -1}
            />
            <Text
              themed
              label="* Gender"
              placeholder="Write here..."
              className="bot-properties-input"
              value={this.state.gender}
              onChange={(value) => this.onUpdate('gender', value)}
              error={this.state.errorKeys.indexOf('gender') > -1}
            />
            <Text
              themed
              label="Name of the Creator / Company"
              placeholder="(Optional) Write here..."
              className="bot-properties-input"
              value={this.state.companyName}
              onChange={(value) => this.onUpdate('companyName', value)}
            />
            <Text
              themed
              label="Name of the app"
              placeholder="(Optional) Write here..."
              className="bot-properties-input"
              value={this.state.appName}
              onChange={(value) => this.onUpdate('appName', value)}
            />
            <Text
              themed
              label="Feedback Smart Action Link"
              placeholder="(Optional) Write here..."
              className="bot-properties-input"
              value={this.state.feedbackSmartActionLink}
              onChange={(value) =>
                this.onUpdate('feedbackSmartActionLink', value)
              }
            />
            <Text
              themed
              label="Company Website"
              placeholder="(Optional) Write here..."
              className="bot-properties-input"
              value={this.state.companyWebsite}
              onChange={(value) => this.onUpdate('companyWebsite', value)}
            />
            <Text
              themed
              label="Where does the bot live?"
              placeholder="(Optional) Write here..."
              className="bot-properties-input"
              value={this.state.botLocation}
              onChange={(value) => this.onUpdate('botLocation', value)}
            />
            <TextArea
              multiline
              themed
              label="Bot Description (Max 300 Words)"
              placeholder="(Optional) Describe the purpose of the bot in a few words here..."
              className="bot-properties-input"
              value={this.state.botContext}
              onChange={(value) => {
                this.onUpdate('botContext', value);
              }}
              error={this.state.errorKeys.indexOf('botContext') > -1}
            />
            <TextArea
              multiline
              themed
              label="Company Description (Max 300 Words)"
              placeholder="(Optional) Write a brief note about the Company, and the services it provides, here..."
              className="bot-properties-input"
              value={this.state.companyContext}
              onChange={(value) =>
                this.onUpdate('companyContext', value.trim())
              }
              error={this.state.errorKeys.indexOf('companyContext') > -1}
            />
            <TextArea
              multiline
              themed
              label="Context Enhancement Instructions (Max 300 Words)"
              placeholder="(Optional) Write a brief instruction you want to provide to GPT during context enhancement."
              className="bot-properties-input"
              value={this.state.contextEnhancementInstr}
              onChange={(value) =>
                this.onUpdate('contextEnhancementInstr', value.trim())
              }
              error={
                this.state.errorKeys.indexOf('contextEnhancementInstr') > -1
              }
              border
            />
            {isAissIntentEnable && (
              <TextArea
                multiline
                themed
                label="Intent Detection Instructions (Max 300 Words)"
                placeholder="(Optional) Write a brief instruction you want to provide to GPT during intent detection."
                className="bot-properties-input"
                value={this.state.intentDetectionInstr}
                onChange={(value) =>
                  this.onUpdate('intentDetectionInstr', value.trim())
                }
                error={
                  this.state.errorKeys.indexOf('intentDetectionInstr') > -1
                }
              />
            )}
            <TextArea
              multiline
              themed
              label="AISS Answering/Inference Instructions (Max 300 Words)"
              placeholder="(Optional) Write a brief instruction you want to provide to GPT during answering/inference."
              className="bot-properties-input"
              value={this.state.answeringInstr}
              onChange={(value) =>
                this.onUpdate('answeringInstr', value.trim())
              }
              error={this.state.errorKeys.indexOf('answeringInstr') > -1}
            />
            {currentDomainInStore.domain_type === 2 && (
              <>
                <Toggle
                  themed
                  value={aiAgentAuthEnabled}
                  onChange={(value) =>
                    this.onUpdate('aiAgentAuthEnabled', value)
                  }
                  label="Authentication"
                />
                <Toggle
                  themed
                  disabled={!aiAgentAuthEnabled}
                  value={this.state.aiAgentSessionTimeoutEnabled}
                  onChange={(value) =>
                    this.onUpdate('aiAgentSessionTimeoutEnabled', value)
                  }
                  label="Session Timeout"
                />
                <Text
                  themed
                  disabled={
                    !aiAgentAuthEnabled ||
                    !this.state.aiAgentSessionTimeoutEnabled
                  }
                  label="Duration of the Session in minutes"
                  placeholder="Session Duration in Minutes"
                  className="bot-properties-input"
                  value={this.state.aiAgentSessionTimeout}
                  onChange={(value) => {
                    if (!value?.trim())
                      this.onUpdate('aiAgentSessionTimeout', null);
                    else this.onUpdate('aiAgentSessionTimeout', value?.trim());
                  }}
                  error={
                    this.state.errorKeys.indexOf('aiAgentSessionTimeout') > -1
                  }
                />
                <Toggle
                  themed
                  value={this.state.addDateAndTimeInPrompt}
                  onChange={(value) =>
                    this.onUpdate('addDateAndTimeInPrompt', value)
                  }
                  label="Keep AI Agent aware of Date and Time"
                />
              </>
            )}
            <Toggle
              themed
              value={this.state.detectLanguageForAllMessages}
              onChange={(value) =>
                this.onUpdate('detectLanguageForAllMessages', value)
              }
              label="Detect language for all messages"
            />

            {currentDomainInStore.domain_type === 2 && (
              <>
                <Toggle
                  themed
                  value={this.state.clearContextOnChatCompletion}
                  onChange={(value) =>
                    this.onUpdate('clearContextOnChatCompletion', value)
                  }
                  label="Clear Context on Chat Completion"
                />
                <Dropdown
                  themed
                  options={[
                    { name: 'None', id: 0 },
                    { name: 'Quick Replies', id: 1 },
                    { name: 'Buttons', id: 2 },
                  ]}
                  className="bot-properties-input"
                  label={
                    <Box padding="0" alignItems="center">
                      <TextDisplay color="black.haptik" variant="h4.semi-bold">
                        Option Display Style
                      </TextDisplay>
                      <Tooltip
                        themed
                        tooltip="Choose how the bot shows selectable options — as buttons or quick replies."
                        position="TOP"
                      >
                        <InfoIconV4
                          size="small"
                          iconColor="grey.dark"
                          marginLeft="4px"
                        />
                      </Tooltip>
                    </Box>
                  }
                  disabled={BotStore.isProductionEnv()}
                  placeholder="Select Formatting"
                  optionKey="name"
                  value={
                    this.state.aIAgentOptionDisplayStyle.name
                      ? this.state.aIAgentOptionDisplayStyle
                      : [
                          { name: 'None', id: 0 },
                          { name: 'Quick Replies', id: 1 },
                          { name: 'Buttons', id: 2 },
                        ].find(
                          (option) =>
                            option.id ===
                            this.state.aIAgentOptionDisplayStyle.id
                        )
                  }
                  onChange={(value) =>
                    this.onUpdate('aIAgentOptionDisplayStyle', value)
                  }
                />
              </>
            )}
            {currentDomainInStore.domain_type === 2 && (
              <>
                <Toggle
                  themed
                  value={aiAgentVoiceEnabled}
                  onChange={(value) =>
                    this.onUpdate('aiAgentVoiceEnabled', value)
                  }
                  label="Enable Voice"
                />
                <Dropdown
                  themed
                  withSearch
                  options={aiAgentVoiceModelOptions || []}
                  className="bot-properties-input"
                  label="Select Model"
                  disabled={BotStore.isProductionEnv() || !aiAgentVoiceEnabled}
                  hint="You can add more languages after the bot has been created."
                  placeholder="Select items"
                  optionKey="name"
                  searchKeys={['name']}
                  value={
                    aIAgentVoiceModel.name
                      ? aIAgentVoiceModel
                      : aiAgentVoiceModelOptions.find(
                          (model) => model.id === aIAgentVoiceModel.id
                        )
                  }
                  onChange={(value) =>
                    this.onUpdate('aIAgentVoiceModel', value)
                  }
                />
                <Dropdown
                  themed
                  withSearch
                  options={telephonyPlatformOptions || []}
                  className="bot-properties-input"
                  label="Select Telephony Provider"
                  disabled={
                    BotStore.isProductionEnv() ||
                    !aiAgentVoiceTelephonyPlatform ||
                    !aiAgentVoiceEnabled
                  }
                  hint="You can add more languages after the bot has been created."
                  placeholder="Select items"
                  optionKey="name"
                  searchKeys={['name']}
                  value={aiAgentVoiceTelephonyPlatform?.name}
                  onChange={(value) => {
                    this.onUpdate('aiAgentVoiceTelephonyPlatform', value);
                    if (
                      !['tata_telephony', 'exotel'].includes(value.name) &&
                      aiAgentHumanAgentTransferEnabled
                    ) {
                      this.onUpdate('aiAgentHumanAgentTransferEnabled', false);
                    }
                  }}
                />

                <Text
                  themed
                  disabled={BotStore.isProductionEnv() || !aiAgentVoiceEnabled}
                  label="Phone Number"
                  placeholder="Phone Number"
                  className="bot-properties-input"
                  value={aiAgentPhoneNumber}
                  onChange={(value) => {
                    if (!value?.trim()) this.onUpdate('aiAgentPhoneNumber', '');
                    else this.onUpdate('aiAgentPhoneNumber', value?.trim());
                  }}
                  error={
                    errorKeys.indexOf('aiAgentPhoneNumber') > -1 &&
                    'Please enter a valid phone number.'
                  }
                  errorText
                />
                <Text
                  themed
                  disabled={
                    BotStore.isProductionEnv() ||
                    !aiAgentVoiceEnabled ||
                    aiAgentVoiceTelephonyPlatform?.name !== 'ozonetel'
                  }
                  label="Sip Registration Number"
                  placeholder="Sip Registration Number"
                  className="bot-properties-input"
                  value={aiAgentOzonetelSipNumber}
                  onChange={(value) => {
                    if (!value?.trim())
                      this.onUpdate('aiAgentOzonetelSipNumber', '');
                    else
                      this.onUpdate('aiAgentOzonetelSipNumber', value?.trim());
                  }}
                />
                <Text
                  themed
                  disabled={BotStore.isProductionEnv() || !aiAgentVoiceEnabled}
                  label="Greeting Message"
                  placeholder="Greeting Message"
                  className="bot-properties-input"
                  value={aiAgentGreetingMessage}
                  onChange={(value) => {
                    if (!value?.trim())
                      this.onUpdate('aiAgentGreetingMessage', '');
                    else this.onUpdate('aiAgentGreetingMessage', value?.trim());
                  }}
                  error={errorKeys.indexOf('aiAgentGreetingMessage') > -1}
                />
                <Toggle
                  themed
                  value={aiAgentTextPipelineEnabled}
                  onChange={(value) =>
                    this.onUpdate('aiAgentTextPipelineEnabled', value)
                  }
                  label="Enable Text Pipeline"
                  disabled={BotStore.isProductionEnv() || !aiAgentVoiceEnabled}
                />
                <Dropdown
                  themed
                  withSearch
                  options={aiAgentVoiceOutputOptions || []}
                  className="bot-properties-input"
                  label="Voice for Output"
                  disabled={BotStore.isProductionEnv() || !aiAgentVoiceEnabled}
                  hint="You can add more languages after the bot has been created."
                  placeholder="Select items"
                  optionKey="name"
                  searchKeys={['name']}
                  value={aiAgentVoiceForOutput || {}}
                  onChange={(value) =>
                    this.onUpdate('aiAgentVoiceForOutput', value)
                  }
                  inputStyles={{
                    borderColor:
                      errorKeys.indexOf('aiAgentVoiceForOutput') > -1 &&
                      '#D91E2D',
                  }}
                  error={
                    errorKeys.indexOf('aiAgentVoiceForOutput') > -1 &&
                    'Please select tts voice for text pipeline.'
                  }
                />
                <Dropdown
                  themed
                  withSearch
                  options={aiAgentVoiceLocaleOptions}
                  className="bot-properties-input"
                  label="Locale"
                  disabled={
                    BotStore.isProductionEnv() ||
                    !aiAgentVoiceEnabled ||
                    !aiAgentVoiceLocaleOptions?.length ||
                    aiAgentTtsEnabled
                  }
                  placeholder="Select locale"
                  optionKey="name"
                  searchKeys={['name']}
                  value={aiAgentVoiceLocale || {}}
                  onChange={(value) =>
                    this.onUpdate('aiAgentVoiceLocale', value)
                  }
                />
                {['tata_telephony', 'exotel'].includes(
                  aiAgentVoiceTelephonyPlatform.name
                ) && (
                  <Toggle
                    themed
                    value={aiAgentHumanAgentTransferEnabled}
                    onChange={(value) =>
                      this.onUpdate('aiAgentHumanAgentTransferEnabled', value)
                    }
                    disabled={
                      BotStore.isProductionEnv() || !aiAgentVoiceEnabled
                    }
                    label={
                      <Box padding="0" alignItems="center">
                        <TextDisplay
                          color="black.haptik"
                          variant="h4.semi-bold"
                        >
                          Allow Transfer to Human Agent
                        </TextDisplay>
                        <Tooltip
                          themed
                          tooltip="When enabled, the Voice AI Agent can transfer the call to a human agent when the user requests it."
                          position="TOP"
                        >
                          <InfoIconV4
                            size="small"
                            iconColor="grey.dark"
                            marginLeft="4px"
                          />
                        </Tooltip>
                      </Box>
                    }
                  />
                )}
                <Toggle
                  themed
                  value={aiAgentToolCallExecutionEnabled}
                  onChange={(value) =>
                    this.onUpdate('aiAgentToolCallExecutionEnabled', value)
                  }
                  disabled={BotStore.isProductionEnv() || !aiAgentVoiceEnabled}
                  label={
                    <Box padding="0" alignItems="center">
                      <TextDisplay color="black.haptik" variant="h4.semi-bold">
                        Action Acknowledgements
                      </TextDisplay>
                      <Tooltip
                        themed
                        tooltip='When enabled, the Voice AI Agent sends a short message to users (e.g., "Stand by please") before it starts executing an action.'
                        position="TOP"
                      >
                        <InfoIconV4
                          size="small"
                          iconColor="grey.dark"
                          marginLeft="4px"
                        />
                      </Tooltip>
                    </Box>
                  }
                />
                {aiAgentVoiceTelephonyPlatform.name === 'freepbx' && (
                  <>
                    <Text
                      themed
                      disabled={
                        BotStore.isProductionEnv() || !aiAgentVoiceEnabled
                      }
                      label="FreePBX ARI Endpoint URL"
                      placeholder="Enter the url"
                      className="bot-properties-input"
                      value={voiceFreePbxAriUrl}
                      onChange={(value) => {
                        if (!value?.trim())
                          this.onUpdate('voiceFreePbxAriUrl', '');
                        else this.onUpdate('voiceFreePbxAriUrl', value?.trim());
                      }}
                      error={errorKeys.indexOf('voiceFreePbxAriUrl') > -1}
                    />
                    <Text
                      themed
                      disabled={
                        BotStore.isProductionEnv() || !aiAgentVoiceEnabled
                      }
                      label="FreePBX ARI Username"
                      placeholder="Enter the username"
                      className="bot-properties-input"
                      value={voiceFreePbxAriUsername}
                      onChange={(value) => {
                        if (!value?.trim())
                          this.onUpdate('voiceFreePbxAriUsername', '');
                        else
                          this.onUpdate(
                            'voiceFreePbxAriUsername',
                            value?.trim()
                          );
                      }}
                      error={errorKeys.indexOf('voiceFreePbxAriUsername') > -1}
                    />
                    <div ref={this.voiceFreePbxAriPasswordWrapperRef}>
                      <Text
                        themed
                        disabled={
                          BotStore.isProductionEnv() || !aiAgentVoiceEnabled
                        }
                        label="FreePBX ARI Password"
                        placeholder="Enter the password"
                        className="bot-properties-input"
                        value={voiceFreePbxAriPassword}
                        onChange={(value) => {
                          if (!value?.trim())
                            this.onUpdate('voiceFreePbxAriPassword', '');
                          else
                            this.onUpdate(
                              'voiceFreePbxAriPassword',
                              value?.trim()
                            );
                        }}
                        postfixIcon={
                          <Box
                            padding="0"
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              this.setState({
                                showVoiceFreePbxAriPassword: !showVoiceFreePbxAriPassword,
                              });
                            }}
                          >
                            {showVoiceFreePbxAriPassword ? (
                              <InvisibleIconV2 tooltip="Hide Password" />
                            ) : (
                              <VisibleIconV2 tooltip="Show Password" />
                            )}
                          </Box>
                        }
                        error={
                          errorKeys.indexOf('voiceFreePbxAriPassword') > -1
                        }
                      />
                    </div>
                    <Text
                      themed
                      disabled={
                        BotStore.isProductionEnv() || !aiAgentVoiceEnabled
                      }
                      label="FreePBX Extension Number"
                      placeholder="Enter extension number"
                      className="bot-properties-input"
                      value={voiceFreePbxExtension}
                      onChange={(value) => {
                        if (!value?.trim())
                          this.onUpdate('voiceFreePbxExtension', '');
                        else
                          this.onUpdate('voiceFreePbxExtension', value?.trim());
                      }}
                      error={errorKeys.indexOf('voiceFreePbxExtension') > -1}
                    />
                  </>
                )}
                <Dropdown
                  themed
                  withSearch
                  options={SUBTLE_NOD_FREQUENCY_OPTIONS}
                  className="bot-properties-input"
                  label={
                    <Box padding="0" alignItems="center">
                      <TextDisplay color="black.haptik" variant="h4.semi-bold">
                        Subtle Nod Frequency
                      </TextDisplay>
                      <Tooltip
                        themed
                        tooltip="Control how often the chatbot gives brief, natural acknowledgements (called “Subtle Nods”) such as “Got it!” or “Sure thing!”. These nods make conversations feel more natural without sounding repetitive."
                        position="TOP"
                      >
                        <InfoIconV4
                          size="small"
                          iconColor="grey.dark"
                          marginLeft="4px"
                        />
                      </Tooltip>
                    </Box>
                  }
                  disabled={
                    BotStore.isProductionEnv() ||
                    !aiAgentVoiceEnabled ||
                    !aiAgentTextPipelineEnabled
                  }
                  placeholder="Select Subtle Nod Frequency"
                  optionKey="name"
                  searchKeys={['name']}
                  value={aiAgentVoiceSubtleNodFrequency || {}}
                  onChange={(value) =>
                    this.onUpdate('aiAgentVoiceSubtleNodFrequency', value)
                  }
                />
                <Box padding="0" flexDirection="column" style={{ gap: '24px' }}>
                  <Box padding="0" alignItems="center">
                    <TextDisplay color="black.haptik" variant="h4.semi-bold">
                      Background Noise Volume
                    </TextDisplay>
                    <Tooltip
                      themed
                      tooltip="Controls volume of the background noise."
                      position="TOP"
                    >
                      <InfoIconV4
                        size="small"
                        iconColor="grey.dark"
                        marginLeft="4px"
                      />
                    </Tooltip>
                  </Box>
                  <Slider
                    min={0}
                    max={1}
                    step={0.05}
                    leftLabel="0"
                    rightLabel="1"
                    labelMarginTop="-7px"
                    value={aiAgentBackgroundNoiseVolume}
                    onChange={(e) =>
                      this.onUpdate(
                        'aiAgentBackgroundNoiseVolume',
                        parseFloat(e.target.value)
                      )
                    }
                    width="80%"
                    style={{ margin: '0 auto' }}
                    disabled={
                      BotStore.isProductionEnv() || !aiAgentVoiceEnabled
                    }
                  />
                </Box>
                {(aiAgentTtsEnabled ||
                  aiAgentVoiceForOutput?.ttsProviderId) && (
                  <Box
                    padding="0"
                    flexDirection="column"
                    style={{ gap: '24px' }}
                  >
                    <Box padding="0" alignItems="center">
                      <TextDisplay color="black.haptik" variant="h4.semi-bold">
                        Speed
                      </TextDisplay>
                      <Tooltip
                        themed
                        tooltip={`Adjusts how fast or slow the generated speech is delivered. (Default: ${speedValue.default} )`}
                        position="TOP"
                      >
                        <InfoIconV4
                          size="small"
                          iconColor="grey.dark"
                          marginLeft="4px"
                        />
                      </Tooltip>
                    </Box>
                    <Slider
                      min={speedValue?.min || 0}
                      max={speedValue?.max || 1}
                      step={speedValue?.step || 0.05}
                      leftLabel={speedValue?.min || 0}
                      rightLabel={speedValue?.max || 1}
                      labelMarginTop="-7px"
                      value={ttsProviderSpeed}
                      onChange={(e) =>
                        this.onUpdate(
                          'ttsProviderSpeed',
                          parseFloat(e.target.value)
                        )
                      }
                      width="80%"
                      style={{ margin: '0 auto' }}
                      disabled={
                        BotStore.isProductionEnv() ||
                        !aiAgentTtsEnabled ||
                        !aiAgentVoiceEnabled
                      }
                    />
                  </Box>
                )}
                {aiAgentVoiceForOutput?.ttsProviderId === 0 && (
                  <>
                    <Box
                      padding="0"
                      flexDirection="column"
                      style={{ gap: '24px' }}
                    >
                      <Box padding="0" alignItems="center">
                        <TextDisplay
                          color="black.haptik"
                          variant="h4.semi-bold"
                        >
                          Stability
                        </TextDisplay>
                        <Tooltip
                          themed
                          tooltip="Controls consistency of tone and pacing across the speech. (Default: 0.5)"
                          position="TOP"
                        >
                          <InfoIconV4
                            size="small"
                            iconColor="grey.dark"
                            marginLeft="4px"
                          />
                        </Tooltip>
                      </Box>
                      <Slider
                        min={0}
                        max={1}
                        step={0.05}
                        leftLabel="0"
                        rightLabel="1"
                        labelMarginTop="-7px"
                        value={ttsProviderStability}
                        onChange={(e) =>
                          this.onUpdate(
                            'ttsProviderStability',
                            parseFloat(e.target.value)
                          )
                        }
                        width="80%"
                        style={{ margin: '0 auto' }}
                        disabled={
                          BotStore.isProductionEnv() ||
                          !aiAgentVoiceForOutput?.name?.includes(
                            'ElevenLabs'
                          ) ||
                          !aiAgentVoiceEnabled
                        }
                      />
                    </Box>
                    <Box
                      padding="0"
                      flexDirection="column"
                      style={{ gap: '24px' }}
                    >
                      <Box padding="0" alignItems="center">
                        <TextDisplay
                          color="black.haptik"
                          variant="h4.semi-bold"
                        >
                          Style
                        </TextDisplay>
                        <Tooltip
                          themed
                          tooltip="Adds expressive variation to the voice. (Default: 0.0)"
                          position="TOP"
                        >
                          <InfoIconV4
                            size="small"
                            iconColor="grey.dark"
                            marginLeft="4px"
                          />
                        </Tooltip>
                      </Box>
                      <Slider
                        min={0}
                        max={1}
                        step={0.05}
                        leftLabel="0"
                        rightLabel="1"
                        labelMarginTop="-7px"
                        value={ttsProviderStyle}
                        onChange={(e) =>
                          this.onUpdate(
                            'ttsProviderStyle',
                            parseFloat(e.target.value)
                          )
                        }
                        width="80%"
                        style={{ margin: '0 auto' }}
                        disabled={
                          BotStore.isProductionEnv() ||
                          !aiAgentVoiceForOutput?.name?.includes(
                            'ElevenLabs'
                          ) ||
                          !aiAgentVoiceEnabled
                        }
                      />
                    </Box>
                    <Box
                      padding="0"
                      flexDirection="column"
                      style={{ gap: '24px' }}
                    >
                      <Box padding="0" alignItems="center">
                        <TextDisplay
                          color="black.haptik"
                          variant="h4.semi-bold"
                        >
                          Similarity Boost
                        </TextDisplay>
                        <Tooltip
                          themed
                          tooltip="Makes the output voice sound closer to the reference voice. (Default: 0.75)"
                          position="TOP"
                        >
                          <InfoIconV4
                            size="small"
                            iconColor="grey.dark"
                            marginLeft="4px"
                          />
                        </Tooltip>
                      </Box>

                      <Slider
                        min={0}
                        max={1}
                        step={0.05}
                        leftLabel="0"
                        rightLabel="1"
                        labelMarginTop="-7px"
                        value={ttsProviderSimilarityBoost}
                        onChange={(e) =>
                          this.onUpdate(
                            'ttsProviderSimilarityBoost',
                            parseFloat(e.target.value)
                          )
                        }
                        width="80%"
                        style={{ margin: '0 auto' }}
                        disabled={
                          BotStore.isProductionEnv() ||
                          !aiAgentVoiceForOutput?.name?.includes(
                            'ElevenLabs'
                          ) ||
                          !aiAgentVoiceEnabled
                        }
                      />
                    </Box>

                    <Toggle
                      themed
                      value={ttsProviderUseSpeakerBoost}
                      onChange={(value) =>
                        this.onUpdate('ttsProviderUseSpeakerBoost', value)
                      }
                      disabled={
                        BotStore.isProductionEnv() ||
                        !aiAgentVoiceForOutput?.name?.includes('ElevenLabs') ||
                        !aiAgentVoiceEnabled
                      }
                      label={
                        <Box padding="0" alignItems="center">
                          <TextDisplay
                            color="black.haptik"
                            variant="h4.semi-bold"
                          >
                            Use Speaker Boost
                          </TextDisplay>
                          <Tooltip
                            themed
                            tooltip="Enhances clarity and presence of the chosen speaker’s voice."
                            position="TOP"
                          >
                            <InfoIconV4
                              size="small"
                              iconColor="grey.dark"
                              marginLeft="4px"
                            />
                          </Tooltip>
                        </Box>
                      }
                    />
                  </>
                )}

                <Box
                  padding="0"
                  id="new-settings"
                  flexDirection="column"
                  style={{ gap: '12px' }}
                >
                  {dynamicSettings?.map((setting) => {
                    const {
                      key,
                      type,
                      title,
                      info_text: infoText,
                      placeholder,
                      visible_when: visibleWhen,
                      disabled,
                    } = setting;
                    const fieldValue = botSettings[key];

                    if (visibleWhen) {
                      const isPresentInNewSettings = dynamicSettings.find(
                        (s) => s.key === visibleWhen.field_key
                      );
                      let value;
                      if (isPresentInNewSettings) {
                        value = botSettings[isPresentInNewSettings.key];
                        if (!visibleWhen.in.includes(value)) return null;
                      } else {
                        const stateKey = keyToStateMap[visibleWhen.field_key];
                        value = this.state[stateKey];
                        if (stateKey?.includes('.')) {
                          const keys = stateKey.split('.');
                          value = this.state[keys[0]]?.[keys[1]];
                        }
                        const isPresentInState =
                          value !== undefined && value !== null;
                        if (
                          !isPresentInState ||
                          !visibleWhen.in.includes(value)
                        )
                          return null;
                      }
                    }

                    const label = (
                      <Box padding="0" alignItems="center">
                        <TextDisplay
                          color="black.haptik"
                          variant="h4.semi-bold"
                        >
                          {title}
                        </TextDisplay>
                        {infoText && (
                          <Tooltip themed tooltip={infoText} position="TOP">
                            <InfoIconV4
                              size="small"
                              iconColor="grey.dark"
                              marginLeft="4px"
                            />
                          </Tooltip>
                        )}
                      </Box>
                    );

                    switch (type) {
                      case 'toggle': {
                        return (
                          <Toggle
                            themed
                            value={fieldValue}
                            onChange={(value) => {
                              this.onUpdate(key, value, true);
                            }}
                            disabled={BotStore.isProductionEnv() || disabled}
                            label={label}
                          />
                        );
                      }

                      case 'dropdown': {
                        return (
                          <Dropdown
                            themed
                            withSearch
                            options={
                              setting.options.map((option) => ({
                                name: option,
                              })) || []
                            }
                            className="bot-properties-input"
                            label={label}
                            disabled={BotStore.isProductionEnv() || disabled}
                            placeholder={placeholder}
                            optionKey="name"
                            searchKeys={['name']}
                            value={{ name: fieldValue }}
                            onChange={(value) => {
                              this.onUpdate(key, value.name, true);
                            }}
                            error={errorKeys.indexOf(key) > -1}
                          />
                        );
                      }

                      case 'text': {
                        return (
                          <Text
                            themed
                            disabled={BotStore.isProductionEnv() || disabled}
                            label={label}
                            placeholder={placeholder}
                            className="bot-properties-input"
                            value={fieldValue}
                            onChange={(value) => {
                              if (!value?.trim()) this.onUpdate(key, '', true);
                              else this.onUpdate(key, value?.trim(), true);
                            }}
                            error={errorKeys.indexOf(key) > -1}
                          />
                        );
                      }

                      case 'slider':
                      case 'range': {
                        return (
                          <Box
                            padding="0"
                            flexDirection="column"
                            style={{ gap: '24px' }}
                          >
                            {label}
                            <Slider
                              min={setting.min || 0}
                              max={setting.max || 1}
                              step={setting.step || 0.05}
                              leftLabel={
                                setting.min_label || `${setting.min}` || '0'
                              }
                              rightLabel={
                                setting.max_label || `${setting.max}` || '1'
                              }
                              labelMarginTop="-7px"
                              value={fieldValue ?? (setting.default || 0)}
                              onChange={(e) =>
                                this.onUpdate(
                                  key,
                                  parseFloat(e.target.value),
                                  true
                                )
                              }
                              width="80%"
                              style={{ margin: '0 auto' }}
                              disabled={BotStore.isProductionEnv() || disabled}
                            />
                          </Box>
                        );
                      }

                      default: {
                        return null;
                      }
                    }
                  })}
                </Box>
              </>
            )}
          </Accordion.Item>
        </Accordion>
      </div>
    );
  }
}
